import React, { type PropsWithChildren, createContext, useEffect, useMemo, useRef, useState } from 'react'

import { activate, initialize, whenAvailable, isAvailable } from '@vp/ab-reader'

import { useAppState } from '../AppContext'

export interface IABTestContext {
  currentExperiments: Record<string, string>
  isAvailable: boolean
}

export const ABTestContext = createContext<IABTestContext>({
  currentExperiments: {},
  isAvailable: false,
})

export type Experiment = {
  key: string
  variations: Record<string, string>
  precheck?: string
  localOverride?: string // for local testing only
}

/**
 * EXPERIMENTS is a record of all the experiments that are available in the page.
 * Each experiment has a key, variations, and an optional precheck function.
 * The localOverride is used for local testing only and should not be used in production.
 *
 * Sample implementation:
 * ```ts
 * const EXPERIMENTS: Record<string, Experiment> = {
 *   EXAMPLE: {
 *     key: "example_key",
 *     variations: {
 *       TEST: "test"
 *     },
 *     precheck: "examplePrecheck", // matches key of testPrechecks
 *     localOverride: "test"
 *   }
 * }
 * ```
 */
export const EXPERIMENTS: Record<string, Experiment> = {
  ADD_OTHER_COLORS: {
    key: 'qsp_add_other_colors',
    variations: {
      TEST: 'test',
      CONTROL: 'control',
    },
    precheck: 'colorPrecheck',
    // localOverride: 'test',
  },
}

const activateExperiment = (experiment: Experiment): string => {
  if (experiment.localOverride) {
    return experiment.localOverride
  }
  return activate(experiment.key) || ''
}

const ABTestContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const hasInitialized = useRef(false)
  if (!hasInitialized.current) {
    initialize()
    hasInitialized.current = true
  }

  const [currentExperiments, setCurrentExperiments] = useState<Record<string, string>>({})
  const [isReaderAvailable, setIsReaderAvailable] = useState<boolean>(false)

  // Example using state variables in precheck
  // const { primaryProject } = useAppState();
  const { isAppStateInitialized, primaryProject } = useAppState()

  /**
   * Prechecks for experiments to determine if they should be activated.
   * Each precheck function should return a boolean indicating if the experiment can be activated.
   *
   * Sample implementation:
   * ```ts
   * const experimentPrechecks: Record<string, () => Promise<boolean>> = {
   *   examplePrecheck: async () => {
   *     if (BffData?.productKey) {
   *       return true;
   *     } else {
   *       return false;
   *     }
   *   }
   * };
   * ```
   */
  const experimentPrechecks: Record<string, () => Promise<boolean>> = {
    colorPrecheck: async () => {
      if (primaryProject?.pageMode !== 'Sized Good') {
        return false
      }
      if (primaryProject?.work.design.editUrl.includes('custom-sportswear-studio')) {
        return false
      }
      return true
    },
  }

  /**
   * use isDepsLoaded to prevent multiple calls for experiment setup
   * return true only if state variables used in prechecks are loaded
   *
   * Sample implementation:
   * ```ts
   * const isDepsLoaded = useMemo(() => {
   *   let allDepsLoaded = false;
   *   if (BffData) allDepsLoaded = true;
   *   return allDepsLoaded;
   * }, [BffData]);
   * ```
   */
  const isDepsLoaded = useMemo(() => {
    let allDepsLoaded = false
    if (isAppStateInitialized && primaryProject) allDepsLoaded = true
    return allDepsLoaded
  }, [isAppStateInitialized, primaryProject])

  useEffect(() => {
    const setUpExperiments = async () => {
      whenAvailable(async (isAvailable) => {
        if (isAvailable) {
          const experiments: Record<string, string> = {}
          await Promise.allSettled(
            Object.values(EXPERIMENTS).map(async (experiment: Experiment) => {
              let precheckPassed = true
              if (experiment.precheck) {
                const precheckFunction = experimentPrechecks[experiment.precheck]
                if (precheckFunction) {
                  precheckPassed = await precheckFunction()
                }
              }
              experiments[experiment.key] = precheckPassed ? activateExperiment(experiment) : ''
            }),
          )
          setCurrentExperiments(experiments)
        }
      }, 2000)
      setIsReaderAvailable(isAvailable())
    }
    if (isDepsLoaded) {
      setUpExperiments()
    }
  }, [isDepsLoaded])

  const contextValue: IABTestContext = useMemo(() => {
    return {
      currentExperiments,
      isAvailable: isReaderAvailable,
    }
  }, [currentExperiments, isReaderAvailable])

  return <ABTestContext.Provider value={contextValue}>{children}</ABTestContext.Provider>
}

export default ABTestContextProvider
